import type { RendererService } from "../render/RendererService";

export interface AdaptiveResolutionConfig {
  targetFps?: number;
  windowSize?: number;
  downThreshold?: number;
  upThreshold?: number;
  scaleStepDown?: number;
  scaleStepUp?: number;
  minScale?: number;
  maxScale?: number;
  cooldownFrames?: number;
}

export class AdaptiveResolution {
  private renderer: RendererService;
  private window = 60;
  private downThresh = 50;
  private upThresh = 58;
  private scaleStepDown = 0.85;
  private scaleStepUp = 0.05;
  private minScale = 0.5;
  private maxScale = 1.0;
  private cooldown = 30;
  private samples: number[] = [];
  private framesSinceChange = 0;
  private enabled = true;

  constructor(renderer: RendererService, cfg?: AdaptiveResolutionConfig) {
    this.renderer = renderer;
    if (cfg) {
      if (cfg.targetFps) this.setTargetFps(cfg.targetFps);
      if (cfg.windowSize) this.window = cfg.windowSize;
      if (cfg.downThreshold) this.downThresh = cfg.downThreshold;
      if (cfg.upThreshold) this.upThresh = cfg.upThreshold;
      if (cfg.scaleStepDown) this.scaleStepDown = cfg.scaleStepDown;
      if (cfg.scaleStepUp) this.scaleStepUp = cfg.scaleStepUp;
      if (cfg.minScale) this.minScale = cfg.minScale;
      if (cfg.maxScale) this.maxScale = cfg.maxScale;
      if (cfg.cooldownFrames) this.cooldown = cfg.cooldownFrames;
    }
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  setTargetFps(fps: number): void {
    this.downThresh = Math.max(10, fps - 10);
    this.upThresh = Math.max(15, fps - 2);
  }

  sample(dt: number): void {
    if (!this.enabled || dt <= 0) return;
    const fps = 1 / dt;
    this.samples.push(fps);
    if (this.samples.length > this.window) this.samples.shift();
    this.framesSinceChange += 1;
    if (this.samples.length < this.window) return;
    if (this.framesSinceChange < this.cooldown) return;

    const avg = this.average();
    const currentScale = this.renderer.getInternalScale();
    if (avg < this.downThresh && currentScale > this.minScale) {
      const next = Math.max(this.minScale, currentScale * this.scaleStepDown);
      this.renderer.setInternalScale(next);
      this.framesSinceChange = 0;
    } else if (avg > this.upThresh && currentScale < this.maxScale) {
      const next = Math.min(this.maxScale, currentScale + this.scaleStepUp);
      this.renderer.setInternalScale(next);
      this.framesSinceChange = 0;
    }
  }

  private average(): number {
    let sum = 0;
    for (const s of this.samples) sum += s;
    return sum / this.samples.length;
  }
}
